import Vue from 'vue'
import { VueConstructor } from 'vue'
import { Ticker } from '@/js/animation/ticker'
import { Manager, Pan, Pinch, Tap, DIRECTION_ALL } from 'hammerjs'

class Rectangle {
  constructor(public x = 0, public y = 0, public width = 0, public height = 0) {}
}

interface Opts {
  $options: {
    click(e: Event): void
    /** The starting position x for gesture events */
    px: number
    /** The starting position y for gesture events */
    py: number
    /** Starting scale for gesture events */
    ss: number
    /** The animation ticker callback manager */
    ticker: Ticker
    /** Hammer manager instance. */
    mc: HammerManager
    /** Original un-zoomed size */
    unzoomed: Rectangle
  }
}
interface Data {
  x: number
  y: number
  tx: number
  ty: number
  naturalWidth: number
  naturalHeight: number
  width: number
  height: number
  tWidth: number
  tHeight: number
  zoomed: boolean
  pointerDown: boolean
  enablePan: boolean
}
function isPath(e: unknown): e is SVGPathElement | SVGPolygonElement | SVGRectElement {
  return (
    e instanceof SVGPathElement || e instanceof SVGPolygonElement || e instanceof SVGRectElement
  )
}

export default (Vue as VueConstructor<Vue & Opts>).extend({
  props: {
    selectedColor: {
      type: String,
      default: 'black',
    },
    fillMode: {
      type: Boolean,
      default: true,
    },
  },
  data(): Data {
    return {
      x: 0,
      y: 0,
      tx: 0,
      ty: 0,
      naturalWidth: 0,
      naturalHeight: 0,
      width: 0,
      height: 0,
      tWidth: 0,
      tHeight: 0,
      zoomed: false,
      pointerDown: false,
      enablePan: true,
    }
  },
  mounted() {
    // this.$el.setAttribute("preserveAspectRatio", "xMidYMin meet"); // removed to allow svg to center vertically
    this.$el.setAttribute('width', `${this.naturalWidth}`)
    this.$el.setAttribute('height', `${this.naturalHeight}`)

    this.$options.ticker = new Ticker()
    this.$options.ticker.subscribe(dt => this.onUpdate(dt))
    this.$options.ticker.start()

    const mc = (this.$options.mc = new Manager(this.$el, { enable: true }))
    const singleTap = new Tap({ event: 'singletap' })
    const doubleTap = new Tap({ event: 'doubletap', taps: 2 })
    doubleTap.recognizeWith(singleTap)
    singleTap.requireFailure(doubleTap)

    mc.add(new Pan({ direction: DIRECTION_ALL }))
    mc.add(new Pinch())
    mc.add(doubleTap)
    mc.add(singleTap)

    // Register the events
    mc.on('pan', e => this.gestureMove(e))
    mc.on('panstart', e => this.gestureStart(e))
    mc.on('panend', e => this.gestureEnd(e))
    mc.on('pinch', e => this.gestureMove(e))
    mc.on('pinchstart', e => this.gestureStart(e))
    mc.on('pinchend', e => this.gestureEnd(e))
    mc.on('movestart', e => this.gestureEnd(e))
    mc.on('singletap', e => this.onClick(e))
    mc.on('doubletap', e => this.onDoubleTap(e))
    // this.setNonFillable();
  },
  beforeDestroy() {
    this.$options.mc.destroy()
    this.$options.ticker.clear()
  },
  computed: {
    viewbox(): string {
      return `${this.x} ${this.y} ${this.width} ${this.height}`
    },
  },
  methods: {
    getPaths() {
      return Array.from(this.$el.querySelectorAll('path, polygon, rect'))
    },
    // setNonFillable() {
    //   const nonFillable = this.getPaths().filter(p => isPath(p) && !p.style.fill);
    //   nonFillable.forEach(p => p.classList.add('n'));
    // },
    randomize() {
      const paths = this.getPaths()
      const fillable = paths.filter(p => {
        return isPath(p) && p.style.fill
      })
      fillable.forEach(p => this.fill(p, `#${Math.floor(Math.random() * 16777215).toString(16)}`))
    },
    setViewportSize(naturalWidth: number, naturalHeight: number) {
      this.x = 0
      this.y = 0
      this.naturalWidth = naturalWidth
      this.naturalHeight = naturalHeight
      this.width = naturalWidth
      this.height = naturalHeight
      this.tx = this.x
      this.ty = this.y
      this.tWidth = this.width
      this.tHeight = this.height
      this.$options.unzoomed = new Rectangle(this.x, this.y, this.width, this.height)
    },
    onDoubleTap(e: HammerInput) {
      if (!this.zoomed) {
        this.tWidth = this.naturalWidth * 0.5
        this.tHeight = this.naturalHeight * 0.5
        this.zoomed = true
      } else {
        this.zoomOut()
      }
      // this.zoomed = !this.zoomed;
    },
    onClick(e: HammerInput): void {
      if (this.fillMode && isPath(e.target) && e.target.style.fill) {
        // Only paint non-black elements
        const prevColor = e.target.style.fill
        this.fill(e.target, this.selectedColor)
        this.$emit('userFill', {
          el: e.target,
          color: this.selectedColor,
          prevColor,
        })
      } else if (!this.fillMode && isPath(e.target)) {
        this.$emit('pick', e.target.style.fill || '#000000')
      }
    },
    fill(el: unknown, color: string): void {
      if (isPath(el)) {
        el.style.fill = color
      }
    },
    gestureStart(e: PointerEventInput) {
      this.pointerDown = true
      this.$options.px = e.center.x
      this.$options.py = e.center.y
      this.$options.ss = e.scale
    },
    gestureEnd(e: PointerEventInput) {
      this.pointerDown = false
    },
    gestureMove(e: PointerEventInput) {
      if (this.pointerDown && this.enablePan) {
        const pixelRatio = window.devicePixelRatio || 1
        const x = e.center.x
        const y = e.center.y
        let dx = this.$options.px - x
        let dy = this.$options.py - y
        const ds = this.$options.ss / e.scale

        // Calculate pinch. Make sure to change dx offset based on how
        // much the width and height have changed.
        const lastWidth = this.tWidth
        const lastHeight = this.tHeight
        this.tWidth *= ds
        this.tHeight *= ds
        if (this.tWidth > this.naturalWidth) {
          this.tWidth = this.naturalWidth
        }
        if (this.tHeight > this.naturalHeight) {
          this.tHeight = this.naturalHeight
        }
        const diffW = lastWidth - this.tWidth
        const diffH = lastHeight - this.tHeight
        dx += diffW * 0.5
        dy += diffH * 0.5

        // Save the last change for next cycle
        this.$options.py = y
        this.$options.px = x
        this.$options.ss = e.scale

        const scale = Math.max(this.tHeight / this.naturalHeight, this.tWidth / this.naturalWidth)
        this.tx += dx * scale * pixelRatio
        this.ty += dy * scale * pixelRatio
        if (this.tx < 0) {
          this.tx = 0
        }
        if (this.tx > this.naturalWidth - this.tWidth) {
          this.tx = this.naturalWidth - this.tWidth
        }
        if (this.ty < 0) {
          this.ty = 0
        }
        if (this.ty > this.naturalHeight - this.tHeight) {
          this.ty = this.naturalHeight - this.tHeight
        }
      }
    },
    onUpdate(dt: number): void {
      const speed = !this.pointerDown ? dt * (4 * window.devicePixelRatio) : 0.5
      // Get the distance between the target and the real setting.
      let distX = this.tx - this.x
      let distY = this.ty - this.y
      let distWidth = this.tWidth - this.width
      let distHeight = this.tHeight - this.height
      if (distX === 0 && distY === 0 && distWidth === 0 && distHeight === 0) {
        return //
      }

      //Easing motion
      //Progressive reduction of distance
      this.x += distX * speed
      this.y += distY * speed
      this.width += distWidth * speed
      this.height += distHeight * speed
      let distanceSq = Math.pow(this.tx - this.x, 2) + Math.pow(this.ty - this.y, 2)

      if (distanceSq < 0.01) {
        this.x = this.tx
        this.y = this.ty
      }
      if (Math.abs(distWidth) < 0.01) {
        this.width = this.tWidth
      }
      if (Math.abs(distHeight) < 0.01) {
        this.height = this.tHeight
      }
    },
    zoomOut() {
      if (this.zoomed) {
        this.zoomed = false
        this.tx = this.$options.unzoomed.x
        this.ty = this.$options.unzoomed.y
        this.tWidth = this.$options.unzoomed.width
        this.tHeight = this.$options.unzoomed.height
      }
    },
    reset() {
      const { x, y, width, height } = this.$options.unzoomed
      this.x = x
      this.y = y
      this.naturalWidth = width
      this.naturalHeight = height
      this.width = width
      this.height = height
      this.tx = x
      this.ty = y
      this.tWidth = width
      this.tHeight = height
    },
  },
})
